import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { getAllComponents, getComponentElement, AppIcon, SkinIconOverrideProvider, useRegistry } from '../../ui';
import type { AppIconName } from '../../ui';
import { ConversationsPage } from '../conversations/ConversationsPage';
import { ChatPage } from '../chat/ChatPage';
import { PresetsPage } from '../presets/PresetsPage';
import { SettingsPage } from '../settings/SettingsPage';
import { AccountPage } from '../account/AccountPage';
import { BillingPage } from '../billing';
import { ProvidersPage } from '../providers';
import { MemoryPage } from '../memory/MemoryPage';
import { GalleryPage } from '../gallery/GalleryPage';
import { TtsPage } from '../mimo/TtsPage';
import { CommunityPage } from '../community';
import { MarketplacePage } from '../marketplace/MarketplacePage';
import { AttachmentsPage } from '../attachments/AttachmentsPage';
import { adminErrorMessage } from '../admin/api';
import {
  adminAssetUrl,
  batchPublish,
  createAdminDraft,
  createAdminSkin,
  deleteAdminAsset,
  deleteAdminSkin,
  getAdminVersion,
  listAdminSkins,
  saveAdminDraft,
  setDefaultSkin,
  uploadAdminAsset,
} from './adminAppearance';
import type { AdminSkin, AdminSkinManifest } from './adminAppearance';
import { componentOverrideCss, skinCssVariables } from './runtime';
import { canonicalSkinId, skinTargetPolicy } from '../../ui/skinTarget';
import type {
  BubbleImageStyle,
  BubbleRole,
  SkinComponentOverride,
  SkinStylePatch,
  SkinTokens,
} from './types';
import styles from './AppearanceAdminPanel.module.css';

type Mode = 'list' | 'editor' | 'publish';
type InspectorTab = 'global' | 'component' | 'assets';
type PreviewPage =
  | 'conversations'
  | 'chat'
  | 'presets'
  | 'settings'
  | 'account'
  | 'billing'
  | 'providers'
  | 'memory'
  | 'gallery'
  | 'tts'
  | 'community'
  | 'marketplace'
  | 'attachments';

interface PreviewState {
  id: string;
  label: string;
  selector?: string;
  index?: number;
  event?: 'click' | 'contextmenu';
}

const PREVIEW_PAGES: {
  id: PreviewPage;
  label: string;
  icon: AppIconName;
  Component: () => ReactElement;
  states: PreviewState[];
}[] = [
  {
    id: 'conversations',
    label: '会话',
    icon: 'conversations',
    Component: ConversationsPage,
    states: [
      { id: 'base', label: '会话列表' },
      { id: 'delete', label: '删除确认', selector: '[data-ui-id^="conversations.item."]', event: 'contextmenu' },
    ],
  },
  {
    id: 'chat',
    label: '对话',
    icon: 'chat',
    Component: ChatPage,
    states: [
      { id: 'base', label: '对话正文' },
      { id: 'controls', label: '上拉参数菜单', selector: '[data-ui-id="chat.controls.handle"]' },
    ],
  },
  {
    id: 'presets',
    label: '角色卡',
    icon: 'presets',
    Component: PresetsPage,
    states: [
      { id: 'base', label: '角色卡列表' },
      { id: 'editor', label: '新建角色卡', selector: '[data-ui-id="presets.createButton"]' },
    ],
  },
  {
    id: 'settings',
    label: '设置',
    icon: 'settings',
    Component: SettingsPage,
    states: [
      { id: 'base', label: '设置主界面' },
      { id: 'appearance', label: '界面/外观', selector: '[data-ui-id="page.settings.appearance.entry"]' },
      { id: 'backup', label: '备份弹窗', selector: '[data-ui-id="page.settings.backup.exportButton"]' },
    ],
  },
  { id: 'account', label: '账户', icon: 'settings', Component: AccountPage, states: [{ id: 'base', label: '账户与余额' }] },
  { id: 'billing', label: '充值', icon: 'settings', Component: BillingPage, states: [{ id: 'base', label: '充值中心' }] },
  {
    id: 'providers',
    label: '提供商',
    icon: 'settings',
    Component: ProvidersPage,
    states: [
      { id: 'base', label: '提供商列表' },
      { id: 'form', label: '新增提供商', selector: '[data-ui-id="providers.header.addButton"]' },
    ],
  },
  {
    id: 'memory',
    label: '记忆',
    icon: 'presets',
    Component: MemoryPage,
    states: [
      { id: 'base', label: '记忆列表' },
      { id: 'editor', label: '新增记忆', selector: '[data-ui-id="memory.newButton"]' },
      { id: 'auto-model', label: '自动记忆模型', selector: '[data-ui-id="memory.autoMemory.modelButton"]' },
    ],
  },
  {
    id: 'gallery',
    label: '图库',
    icon: 'gallery',
    Component: GalleryPage,
    states: [
      { id: 'base', label: '图库列表' },
      { id: 'preview', label: '图片预览', selector: '[data-ui-id^="gallery.item."]' },
      { id: 'clear', label: '清空确认', selector: '[data-ui-id="gallery.clearButton"]' },
    ],
  },
  {
    id: 'tts',
    label: '语音与 TTS',
    icon: 'chat',
    Component: TtsPage,
    states: [
      { id: 'base', label: '语音回复显示' },
      { id: 'design', label: '音色设计', selector: '[role="tab"]', index: 1 },
      { id: 'clone', label: '音色复刻', selector: '[role="tab"]', index: 2 },
    ],
  },
  {
    id: 'community',
    label: '角色卡社区',
    icon: 'presets',
    Component: CommunityPage,
    states: [
      { id: 'base', label: '社区列表' },
      { id: 'detail', label: '角色卡详情', selector: '[data-ui-id^="community.card."]' },
    ],
  },
  { id: 'marketplace', label: '模型广场', icon: 'settings', Component: MarketplacePage, states: [{ id: 'base', label: '模型广场' }] },
  { id: 'attachments', label: '附件', icon: 'attach', Component: AttachmentsPage, states: [{ id: 'base', label: '附件演示页' }] },
];

function cloneTokens(tokens: SkinTokens): SkinTokens {
  return JSON.parse(JSON.stringify(tokens)) as SkinTokens;
}

function statusLabel(status: 'draft' | 'published'): string {
  return status === 'draft' ? '草稿' : '已发布';
}

function skinSlug(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `skin-${Date.now()}`;
}

function defaultBubbleImage(role: BubbleRole): BubbleImageStyle {
  return {
    slot: role,
    sliceTop: 24,
    sliceRight: 24,
    sliceBottom: 24,
    sliceLeft: 24,
    width: 24,
    repeat: 'stretch',
    paddingTop: 12,
    paddingRight: 14,
    paddingBottom: 12,
    paddingLeft: 14,
  };
}

function Field({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}): ReactElement {
  return <label className={wide ? `${styles.field} ${styles.fullField}` : styles.field}><span>{label}</span>{children}</label>;
}

function NumberField({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
}: {
  label: string;
  value: number | undefined;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number | undefined) => void;
}): ReactElement {
  return (
    <Field label={label}>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value ?? ''}
        onChange={(event) => onChange(event.currentTarget.value === '' ? undefined : Number(event.currentTarget.value))}
      />
    </Field>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): ReactElement {
  return (
    <Field label={label}>
      <span className={styles.colorInput}>
        <input type="color" value={value} onChange={(event) => onChange(event.currentTarget.value)} />
        <code>{value.toUpperCase()}</code>
      </span>
    </Field>
  );
}

function PreviewSurface({
  tokens,
  page,
  state,
  selectMode,
  selectedId,
  onSelect,
  rootRef,
  wallpaperUrl,
  iconSources,
  bubbleSources,
  fontFaces,
}: {
  tokens: SkinTokens;
  page: PreviewPage;
  state: PreviewState;
  selectMode: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  rootRef: { current: HTMLDivElement | null };
  wallpaperUrl: string | null;
  iconSources: Record<string, string>;
  bubbleSources: Partial<Record<BubbleRole, string>>;
  fontFaces: string;
}): ReactElement {
  const entry = PREVIEW_PAGES.find((item) => item.id === page) ?? PREVIEW_PAGES[0]!;
  const PageComponent = entry.Component;
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0.68);
  const variables = skinCssVariables(tokens);
  variables['--skin-wallpaper-image'] = wallpaperUrl ? `url("${wallpaperUrl}")` : 'none';
  for (const role of ['user', 'assistant', 'system'] as const) {
    variables[`--bubble-${role}-image`] = bubbleSources[role] ? `url("${bubbleSources[role]}")` : 'none';
  }
  if (fontFaces.includes('ZxPreview-body')) variables['--font-body'] = '"ZxPreview-body", system-ui, sans-serif';
  if (fontFaces.includes('ZxPreview-heading')) variables['--font-heading'] = '"ZxPreview-heading", system-ui, sans-serif';
  const [transientCss, setTransientCss] = useState('');

  useEffect(() => {
    setTransientCss(componentOverrideCss(tokens.componentOverrides, '#skin-editor-preview '));
  }, [tokens.componentOverrides]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLElement>('[data-skin-original-text]').forEach((element) => {
      element.textContent = element.dataset.skinOriginalText ?? element.textContent;
    });
    for (const [componentId, override] of Object.entries(tokens.componentOverrides)) {
      if (override.text === undefined) continue;
      root.querySelectorAll<HTMLElement>('[data-skin-editable-text="true"]').forEach((element) => {
        if (element.dataset.skinId !== componentId || element.childElementCount > 0) return;
        element.dataset.skinOriginalText ??= element.textContent ?? '';
        element.textContent = override.text ?? '';
      });
    }
  }, [rootRef, tokens.componentOverrides, page, state.id]);

  useEffect(() => {
    const node = boxRef.current;
    if (!node) return;
    const update = (): void => {
      const width = node.clientWidth;
      const height = node.clientHeight;
      if (width <= 0 || height <= 0) return;
      setScale(Math.max(0.25, Math.min(0.9, (width - 16) / 390, (height - 16) / 844)));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (selectMode || state.selector === undefined) return;
    const timer = window.setTimeout(() => {
      const root = rootRef.current;
      const matches = root?.querySelectorAll<HTMLElement>(state.selector ?? '');
      const target = matches?.[state.index ?? 0];
      if (state.event === 'contextmenu') {
        target?.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true }));
      } else {
        target?.click();
      }
    }, 180);
    return () => window.clearTimeout(timer);
  }, [rootRef, selectMode, state]);

  return (
    <div ref={boxRef} className={styles.previewBox}>
      <div
        id="skin-editor-preview"
        ref={rootRef}
        className={selectMode ? `${styles.previewViewport} ${styles.selectMode}` : styles.previewViewport}
        style={{ ...variables, width: 390 * scale, height: 844 * scale }}
        onClickCapture={(event) => {
          if (!selectMode) return;
          const target = (event.target as HTMLElement).closest<HTMLElement>('[data-skin-id]');
          if (!target) return;
          event.preventDefault();
          event.stopPropagation();
          onSelect(target.dataset.skinId ?? '');
        }}
      >
        <style>{transientCss}</style>
        <style>{fontFaces}</style>
        <div
          className={styles.previewCanvas}
          style={{
            width: 390,
            height: 844,
            transform: `scale(${String(scale)})`,
            backgroundImage: 'var(--skin-wallpaper-image), var(--skin-background-gradient)',
            backgroundSize: 'var(--skin-wallpaper-fit), cover',
            backgroundPosition: 'var(--skin-wallpaper-position), center',
            backgroundColor: 'var(--skin-background)',
          }}
        >
          <SkinIconOverrideProvider value={iconSources}>
            <div
              className={styles.previewContent}
              data-selected-id={selectedId ?? undefined}
              style={{ pointerEvents: selectMode ? 'auto' : 'none' }}
            >
              <PageComponent key={`${page}-${state.id}`} />
            </div>
          </SkinIconOverrideProvider>
          <div className={styles.previewNav}>
            {PREVIEW_PAGES.slice(0, 4).map((item) => {
              const active = item.id === page;
              return (
                <span key={item.id} className={active ? styles.previewNavActive : styles.previewNavItem}>
                  <AppIcon name={item.icon} size={18} />
                  <small>{item.label}</small>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ComponentInspector({
  skinId,
  label,
  tokens,
  onChange,
  onReset,
}: {
  skinId: string;
  label: string;
  tokens: SkinTokens;
  onChange: (next: SkinTokens) => void;
  onReset: () => void;
}): ReactElement {
  const override = tokens.componentOverrides[skinId] ?? {};
  const meta = getAllComponents().find((entry) => canonicalSkinId(entry.id) === skinId);
  const policy = meta ? skinTargetPolicy(meta) : {
    selectable: true,
    hideable: true,
    editableText: false,
    reason: null,
  };
  const update = (patch: Partial<SkinComponentOverride>): void => {
    const next = cloneTokens(tokens);
    next.componentOverrides[skinId] = { ...(next.componentOverrides[skinId] ?? {}), ...patch };
    onChange(next);
  };
  const style = (patch: Partial<SkinStylePatch>): void => {
    update({ style: { ...(override.style ?? {}), ...patch } });
  };
  return (
    <section className={styles.inspector}>
      <div className={styles.inspectorHead}>
        <div>
          <strong>{label}</strong>
          <small>{skinId}</small>
        </div>
        <button type="button" onClick={onReset}>恢复本组件</button>
      </div>
      <div className={styles.fieldGrid}>
        <Field label="背景" wide>
          <input type="text" placeholder="#17212b / rgba(...)" value={override.style?.background ?? ''} onChange={(event) => style({ background: event.currentTarget.value || undefined })} />
        </Field>
        <ColorField label="文字色" value={toColor(override.style?.color, tokens.palette.text)} onChange={(color) => style({ color })} />
        <ColorField label="边框色" value={toColor(override.style?.borderColor, tokens.palette.border)} onChange={(borderColor) => style({ borderColor })} />
        <NumberField label="边框宽度" min={0} max={32} value={override.style?.borderWidth} onChange={(borderWidth) => style({ borderWidth })} />
        <Field label="边框样式">
          <select value={override.style?.borderStyle ?? 'solid'} onChange={(event) => style({ borderStyle: event.currentTarget.value as SkinStylePatch['borderStyle'] })}>
            <option value="none">无</option>
            <option value="solid">实线</option>
            <option value="dashed">虚线</option>
            <option value="dotted">点线</option>
          </select>
        </Field>
        <NumberField label="圆角" min={0} max={999} value={override.style?.borderRadius} onChange={(borderRadius) => style({ borderRadius })} />
        <NumberField label="水平内边距" min={0} max={160} value={override.style?.paddingX} onChange={(paddingX) => style({ paddingX })} />
        <NumberField label="垂直内边距" min={0} max={160} value={override.style?.paddingY} onChange={(paddingY) => style({ paddingY })} />
        <NumberField label="元素间距" min={0} max={80} value={override.style?.gap} onChange={(gap) => style({ gap })} />
        <NumberField label="透明度" min={0} max={1} step={0.05} value={override.style?.opacity} onChange={(opacity) => style({ opacity })} />
        <NumberField label="毛玻璃模糊" min={0} max={40} value={override.style?.blur} onChange={(blur) => style({ blur })} />
        <NumberField label="饱和度" min={0} max={200} value={override.style?.saturation} onChange={(saturation) => style({ saturation })} />
        <Field label="阴影" wide>
          <input type="text" placeholder="0 12px 32px rgba(...)" value={override.style?.boxShadow ?? ''} onChange={(event) => style({ boxShadow: event.currentTarget.value || undefined })} />
        </Field>
        <Field label="字体">
          <input type="text" value={override.style?.fontFamily ?? ''} onChange={(event) => style({ fontFamily: event.currentTarget.value || undefined })} />
        </Field>
        <NumberField label="字号" min={8} max={72} value={override.style?.fontSize} onChange={(fontSize) => style({ fontSize })} />
        <NumberField label="字重" min={100} max={900} step={100} value={override.style?.fontWeight} onChange={(fontWeight) => style({ fontWeight })} />
        <NumberField label="行高" min={0.8} max={3} step={0.1} value={override.style?.lineHeight} onChange={(lineHeight) => style({ lineHeight })} />
        <NumberField label="字距" min={-4} max={12} step={0.1} value={override.style?.letterSpacing} onChange={(letterSpacing) => style({ letterSpacing })} />
        <Field label="文字对齐">
          <select value={override.style?.textAlign ?? 'left'} onChange={(event) => style({ textAlign: event.currentTarget.value as SkinStylePatch['textAlign'] })}>
            <option value="left">左对齐</option>
            <option value="center">居中</option>
            <option value="right">右对齐</option>
            <option value="justify">两端对齐</option>
          </select>
        </Field>
        <ColorField label="图标色" value={toColor(override.style?.iconColor, tokens.palette.primary)} onChange={(iconColor) => style({ iconColor })} />
        <NumberField label="图标尺寸" min={8} max={128} value={override.style?.iconSize} onChange={(iconSize) => style({ iconSize })} />
      </div>
      {policy.editableText ? (
        <Field label="静态文案" wide>
          <input type="text" maxLength={240} value={override.text ?? ''} onChange={(event) => update({ text: event.currentTarget.value || undefined })} />
        </Field>
      ) : null}
      <label className={styles.switchRow}>
        隐藏组件
        <input
          type="checkbox"
          disabled={!policy.hideable}
          checked={override.hidden === true}
          onChange={(event) => update({ hidden: event.currentTarget.checked })}
        />
      </label>
      {policy.reason ? <p className={styles.lockHint}>{policy.reason}</p> : null}
    </section>
  );
}

function toColor(value: string | undefined, fallback: string): string {
  return /^#[0-9a-f]{6}$/i.test(value ?? '') ? value! : /^#[0-9a-f]{6}$/i.test(fallback) ? fallback : '#6ba7e5';
}

function TokenInspector({
  tokens,
  onChange,
  onUploadBubble,
}: {
  tokens: SkinTokens;
  onChange: (next: SkinTokens) => void;
  onUploadBubble: (role: BubbleRole, file: File | undefined) => void;
}): ReactElement {
  const patch = (update: (next: SkinTokens) => void): void => {
    const next = cloneTokens(tokens);
    update(next);
    onChange(next);
  };
  const paletteFields: { key: Exclude<keyof SkinTokens['palette'], 'backgroundGradient'>; label: string }[] = [
    { key: 'background', label: '背景底色' },
    { key: 'panel', label: '面板基础色' },
    { key: 'control', label: '控件基础色' },
    { key: 'overlay', label: '弹层基础色' },
    { key: 'primary', label: '主题色' },
    { key: 'primaryPressed', label: '按下色' },
    { key: 'text', label: '主文字' },
    { key: 'textSecondary', label: '次文字' },
    { key: 'border', label: '边框' },
    { key: 'danger', label: '危险色' },
    { key: 'backdrop', label: '遮罩色' },
  ];
  const radii: { key: keyof SkinTokens['radii']; label: string }[] = [
    { key: 'xs', label: 'XS' },
    { key: 'sm', label: 'SM' },
    { key: 'md', label: 'MD' },
    { key: 'lg', label: 'LG' },
    { key: 'full', label: 'Full' },
  ];
  const typeSizes: { key: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; label: string }[] = [
    { key: 'xs', label: 'XS' },
    { key: 'sm', label: 'SM' },
    { key: 'md', label: 'MD' },
    { key: 'lg', label: 'LG' },
    { key: 'xl', label: 'XL' },
  ];
  const updateBubble = (role: BubbleRole, update: Partial<BubbleImageStyle>): void => {
    patch((next) => {
      const current = next.bubbleImages?.[role] ?? defaultBubbleImage(role);
      next.bubbleImages = { ...(next.bubbleImages ?? {}), [role]: { ...current, ...update } };
    });
  };
  return (
    <div className={styles.tokenInspector}>
      <section className={styles.tokenSection}>
        <div className={styles.sectionHead}><h4>主题与调色板</h4><span>全局颜色令牌</span></div>
        <div className={styles.fieldGrid}>
          {paletteFields.map((field) => (
            <ColorField
              key={field.key}
              label={field.label}
              value={toColor(tokens.palette[field.key], '#000000')}
              onChange={(value) => patch((next) => { next.palette[field.key] = value; })}
            />
          ))}
        </div>
        <Field label="背景渐变" wide>
          <textarea
            rows={3}
            value={tokens.palette.backgroundGradient ?? ''}
            onChange={(event) => patch((next) => { next.palette.backgroundGradient = event.currentTarget.value || undefined; })}
          />
        </Field>
      </section>

      <section className={styles.tokenSection}>
        <div className={styles.sectionHead}><h4>毛玻璃</h4><span>统一主题着色</span></div>
        <ColorField
          label="玻璃着色"
          value={toColor(tokens.glass.tintColor, tokens.palette.primary)}
          onChange={(value) => patch((next) => { next.glass.tintColor = value; })}
        />
        <div className={styles.fieldGrid}>
          <NumberField label="着色强度" min={0} max={60} value={tokens.glass.tintStrength ?? 18} onChange={(value) => patch((next) => { next.glass.tintStrength = value ?? 0; })} />
          <NumberField label="面板透明度" min={0} max={100} value={tokens.glass.panelTransparency} onChange={(value) => patch((next) => { next.glass.panelTransparency = value ?? 0; })} />
          <NumberField label="控件透明度" min={0} max={100} value={tokens.glass.controlTransparency} onChange={(value) => patch((next) => { next.glass.controlTransparency = value ?? 0; })} />
          <NumberField label="弹层透明度" min={0} max={100} value={tokens.glass.overlayTransparency} onChange={(value) => patch((next) => { next.glass.overlayTransparency = value ?? 0; })} />
          <NumberField label="模糊半径" min={0} max={40} value={tokens.glass.blurPx} onChange={(value) => patch((next) => { next.glass.blurPx = value ?? 0; })} />
          <NumberField label="饱和度" min={0} max={200} value={tokens.glass.saturation} onChange={(value) => patch((next) => { next.glass.saturation = value ?? 0; })} />
        </div>
      </section>

      <section className={styles.tokenSection}>
        <div className={styles.sectionHead}><h4>气泡</h4><span>颜色与九宫格图片</span></div>
        <div className={styles.fieldGrid}>
          <ColorField label="自己气泡" value={toColor(tokens.bubbles.user, '#3f7fb8')} onChange={(value) => patch((next) => { next.bubbles.user = value; })} />
          <ColorField label="助手气泡" value={toColor(tokens.bubbles.assistant, '#243240')} onChange={(value) => patch((next) => { next.bubbles.assistant = value; })} />
          <ColorField label="系统气泡" value={toColor(tokens.bubbles.system, '#17212b')} onChange={(value) => patch((next) => { next.bubbles.system = value; })} />
        </div>
        {(['user', 'assistant', 'system'] as const).map((role) => {
          const image = tokens.bubbleImages?.[role];
          return (
            <div key={role} className={styles.bubbleEditor}>
              <div className={styles.inspectorHead}>
                <div><strong>{role === 'user' ? '自己气泡图片' : role === 'assistant' ? '助手气泡图片' : '系统气泡图片'}</strong><small>PNG / SVG，九宫格切片</small></div>
                {image ? <button type="button" onClick={() => patch((next) => { if (next.bubbleImages) delete next.bubbleImages[role]; })}>移除设置</button> : null}
              </div>
              <Field label="气泡图片">
                <input
                  type="file"
                  accept="image/png,image/svg+xml"
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    event.currentTarget.value = '';
                    if (file) {
                      patch((next) => {
                        next.bubbleImages = { ...(next.bubbleImages ?? {}), [role]: { ...defaultBubbleImage(role), ...(image ?? {}) } };
                      });
                      onUploadBubble(role, file);
                    }
                  }}
                />
              </Field>
              {image ? (
                <>
                  <div className={styles.fieldGrid}>
                    <NumberField label="上切片" min={0} max={512} value={image.sliceTop} onChange={(value) => updateBubble(role, { sliceTop: value ?? 0 })} />
                    <NumberField label="右切片" min={0} max={512} value={image.sliceRight} onChange={(value) => updateBubble(role, { sliceRight: value ?? 0 })} />
                    <NumberField label="下切片" min={0} max={512} value={image.sliceBottom} onChange={(value) => updateBubble(role, { sliceBottom: value ?? 0 })} />
                    <NumberField label="左切片" min={0} max={512} value={image.sliceLeft} onChange={(value) => updateBubble(role, { sliceLeft: value ?? 0 })} />
                    <NumberField label="边框宽度" min={0} max={160} value={image.width} onChange={(value) => updateBubble(role, { width: value ?? 0 })} />
                    <Field label="平铺">
                      <select value={image.repeat} onChange={(event) => updateBubble(role, { repeat: event.currentTarget.value as BubbleImageStyle['repeat'] })}>
                        <option value="stretch">拉伸</option>
                        <option value="round">圆铺</option>
                        <option value="repeat">重复</option>
                      </select>
                    </Field>
                    <NumberField label="上内边距" min={0} max={160} value={image.paddingTop} onChange={(value) => updateBubble(role, { paddingTop: value ?? 0 })} />
                    <NumberField label="右内边距" min={0} max={160} value={image.paddingRight} onChange={(value) => updateBubble(role, { paddingRight: value ?? 0 })} />
                    <NumberField label="下内边距" min={0} max={160} value={image.paddingBottom} onChange={(value) => updateBubble(role, { paddingBottom: value ?? 0 })} />
                    <NumberField label="左内边距" min={0} max={160} value={image.paddingLeft} onChange={(value) => updateBubble(role, { paddingLeft: value ?? 0 })} />
                  </div>
                </>
              ) : null}
            </div>
          );
        })}
      </section>

      <section className={styles.tokenSection}>
        <div className={styles.sectionHead}><h4>图标、圆角与字体</h4><span>全局外观</span></div>
        <label className={styles.switchRow}>图标跟随主题色
          <input type="checkbox" checked={tokens.icons.followTheme !== false} onChange={(event) => patch((next) => { next.icons.followTheme = event.currentTarget.checked; })} />
        </label>
        <div className={styles.fieldGrid}>
          <ColorField label="图标颜色" value={toColor(tokens.icons.color, '#6ba7e5')} onChange={(value) => patch((next) => { next.icons.color = value; })} />
          <ColorField label="选中背景" value={toColor(tokens.icons.activeBackground, '#17212b')} onChange={(value) => patch((next) => { next.icons.activeBackground = value; })} />
          <NumberField label="描边宽度" min={0.5} max={4} step={0.25} value={tokens.icons.strokeWidth} onChange={(value) => patch((next) => { next.icons.strokeWidth = value ?? 1; })} />
          {radii.map((item) => (
            <NumberField key={item.key} label={`圆角 ${item.label}`} min={0} max={999} value={tokens.radii[item.key]} onChange={(value) => patch((next) => { next.radii[item.key] = value ?? 0; })} />
          ))}
          <Field label="正文字体">
            <input value={tokens.typography.bodyFamily} onChange={(event) => patch((next) => { next.typography.bodyFamily = event.currentTarget.value; })} />
          </Field>
          <Field label="标题字体">
            <input value={tokens.typography.headingFamily} onChange={(event) => patch((next) => { next.typography.headingFamily = event.currentTarget.value; })} />
          </Field>
          {typeSizes.map((item) => (
            <NumberField key={item.key} label={`字号 ${item.label}`} min={8} max={80} value={tokens.typography[item.key]} onChange={(value) => patch((next) => { next.typography[item.key] = value ?? 8; })} />
          ))}
          <Field label="低阴影" wide>
            <input value={tokens.shadows.low} onChange={(event) => patch((next) => { next.shadows.low = event.currentTarget.value; })} />
          </Field>
          <Field label="高阴影" wide>
            <input value={tokens.shadows.high} onChange={(event) => patch((next) => { next.shadows.high = event.currentTarget.value; })} />
          </Field>
          <Field label="壁纸适配">
            <select value={tokens.wallpaper.fit} onChange={(event) => patch((next) => { next.wallpaper.fit = event.currentTarget.value as SkinTokens['wallpaper']['fit']; })}>
              <option value="cover">铺满</option>
              <option value="contain">完整显示</option>
            </select>
          </Field>
          <Field label="壁纸位置">
            <input value={tokens.wallpaper.position} onChange={(event) => patch((next) => { next.wallpaper.position = event.currentTarget.value; })} />
          </Field>
        </div>
      </section>
    </div>
  );
}

export function AppearanceAdminPanel(): ReactElement {
  const [mode, setMode] = useState<Mode>('list');
  const [skins, setSkins] = useState<AdminSkin[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editorSkin, setEditorSkin] = useState<AdminSkin | null>(null);
  const [manifest, setManifest] = useState<AdminSkinManifest | null>(null);
  const [tokens, setTokens] = useState<SkinTokens | null>(null);
  const [dirty, setDirty] = useState(false);
  const [history, setHistory] = useState<SkinTokens[]>([]);
  const [releaseNotes, setReleaseNotes] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [componentQuery, setComponentQuery] = useState('');
  const [iconSlot, setIconSlot] = useState('conversations');
  const [selectedPublish, setSelectedPublish] = useState<string[]>([]);
  const [previewPage, setPreviewPage] = useState<PreviewPage>('chat');
  const [previewStateId, setPreviewStateId] = useState('base');
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>('global');
  const [discovered, setDiscovered] = useState<{ id: string; label: string; page: string }[]>([]);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const registry = useRegistry();

  const load = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      setSkins(await listAdminSkins());
    } catch (failure) {
      setError(adminErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (registry.length === 0) return;
    setDiscovered((current) => {
      const map = new Map(current.map((item) => [item.id, item]));
      for (const meta of registry) {
        const id = canonicalSkinId(meta.id);
        if (!map.has(id)) map.set(id, { id, label: meta.label, page: meta.page });
      }
      return [...map.values()];
    });
  }, [registry]);

  const previewStates = useMemo(
    () => PREVIEW_PAGES.find((item) => item.id === previewPage)?.states ?? [{ id: 'base', label: '默认' }],
    [previewPage],
  );
  const previewState = previewStates.find((item) => item.id === previewStateId) ?? previewStates[0]!;

  useEffect(() => {
    if (!previewStates.some((item) => item.id === previewStateId)) setPreviewStateId(previewStates[0]?.id ?? 'base');
  }, [previewPage, previewStateId, previewStates]);

  async function createSkin(): Promise<void> {
    const name = window.prompt('新皮肤名称');
    if (!name) return;
    setBusy(true);
    try {
      const created = await createAdminSkin({ name, slug: skinSlug(name), description: '', sortOrder: skins.length });
      await load();
      await openEditor({ ...(await listAdminSkins()).find((item) => item.id === created.id)! });
    } catch (failure) {
      setError(adminErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  }

  async function openEditor(skin: AdminSkin): Promise<void> {
    setBusy(true);
    setError('');
    try {
      const draft = skin.latestStatus === 'draft'
        ? { version: skin.latestVersion }
        : await createAdminDraft(skin.id);
      const loaded = await getAdminVersion(skin.id, draft.version);
      setEditorSkin(skin);
      setManifest(loaded);
      setTokens(cloneTokens(loaded.tokens));
      setDirty(false);
      setHistory([]);
      setReleaseNotes(loaded.version.releaseNotes);
      setSelectedId(null);
      setSelectedLabel('');
      setInspectorTab('global');
      setMode('editor');
    } catch (failure) {
      setError(adminErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  }

  function commit(next: SkinTokens): void {
    if (tokens) setHistory((previous) => [...previous.slice(-29), cloneTokens(tokens)]);
    setTokens(next);
    setDirty(true);
    setNotice('');
  }

  function undo(): void {
    const previous = history[history.length - 1];
    if (!previous) return;
    setTokens(previous);
    setHistory((items) => items.slice(0, -1));
    setDirty(true);
  }

  async function save(): Promise<void> {
    if (!editorSkin || !manifest || !tokens) return;
    setBusy(true);
    setError('');
    try {
      await saveAdminDraft(editorSkin.id, manifest.version.version, tokens, releaseNotes);
      setDirty(false);
      setNotice('草稿已保存');
      const refreshed = await getAdminVersion(editorSkin.id, manifest.version.version);
      setManifest(refreshed);
      setTokens(cloneTokens(refreshed.tokens));
      await load();
    } catch (failure) {
      setError(adminErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  }

  function exitEditor(): void {
    if (dirty && !window.confirm('有未保存修改，确定退出编辑器吗？')) return;
    setMode('list');
    setSelectedId(null);
    setSelectMode(false);
  }

  async function upload(
    kind: 'wallpaper' | 'preview' | 'font' | 'icon' | 'bubble',
    slot: string,
    file: File | undefined,
  ): Promise<void> {
    if (!file || !editorSkin || !manifest) return;
    setBusy(true);
    setError('');
    try {
      await uploadAdminAsset(editorSkin.id, manifest.version.version, kind, slot, file);
      const refreshed = await getAdminVersion(editorSkin.id, manifest.version.version);
      setManifest(refreshed);
      setNotice(`${kind === 'wallpaper' ? '背景' : kind === 'preview' ? '预览图' : kind === 'font' ? '字体' : kind === 'icon' ? '图标' : '气泡图片'}已上传`);
      await load();
    } catch (failure) {
      setError(adminErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  }

  async function publish(action: 'publish' | 'unpublish'): Promise<void> {
    if (selectedPublish.length === 0) return;
    setBusy(true);
    setError('');
    try {
      const result = await batchPublish(selectedPublish, action, releaseNotes);
      const failed = result.results.filter((entry) => !entry.ok);
      setNotice(`已完成 ${String(result.results.length - failed.length)} 套${action === 'publish' ? '上架' : '下架'}` +
        (failed.length ? `；失败：${failed.map((entry) => skins.find((skin) => skin.id === entry.skinId)?.name ?? entry.skinId).join('、')}` : ''));
      setSelectedPublish([]);
      await load();
    } catch (failure) {
      setError(adminErrorMessage(failure));
    } finally {
      setBusy(false);
    }
  }

  const componentOptions = useMemo(() => {
    const seen = new Map<string, { id: string; label: string; page: string }>();
    for (const entry of discovered) {
      const key = canonicalSkinId(entry.id);
      if (!seen.has(key)) seen.set(key, { ...entry, id: key });
    }
    for (const meta of getAllComponents()) {
      const key = canonicalSkinId(meta.id);
      const element = getComponentElement(meta.id);
      if (!element || !previewRef.current?.contains(element)) continue;
      if (!seen.has(key)) seen.set(key, { id: key, label: meta.label, page: meta.page });
    }
    const query = componentQuery.trim().toLowerCase();
    return [...seen.values()]
      .filter((entry) => query === '' || entry.id.toLowerCase().includes(query) || entry.label.toLowerCase().includes(query) || entry.page.toLowerCase().includes(query))
      .sort((a, b) => a.page.localeCompare(b.page) || a.label.localeCompare(b.label))
      .slice(0, 240);
  }, [componentQuery, discovered, previewRef]);

  const previewAssets = useMemo(() => {
    const wallpaper = manifest?.assets.find((asset) => asset.kind === 'wallpaper');
    const icons: Record<string, string> = {};
    const bubbles: Partial<Record<BubbleRole, string>> = {};
    const fontRules: string[] = [];
    for (const asset of manifest?.assets ?? []) {
      if (asset.kind === 'icon') icons[asset.slot] = `url("${adminAssetUrl(asset.url)}")`;
      if (asset.kind === 'bubble' && (asset.slot === 'user' || asset.slot === 'assistant' || asset.slot === 'system')) {
        bubbles[asset.slot] = adminAssetUrl(asset.url);
      }
      if (asset.kind === 'font') {
        fontRules.push(`@font-face{font-family:"ZxPreview-${asset.slot}";src:url("${adminAssetUrl(asset.url)}") format("woff2");font-display:swap;}`);
      }
    }
    return {
      wallpaperUrl: wallpaper ? adminAssetUrl(wallpaper.url) : null,
      iconSources: icons,
      bubbleSources: bubbles,
      fontFaces: fontRules.join('\n'),
    };
  }, [manifest?.assets]);

  if (mode === 'list') {
    return (
      <section className={styles.panel}>
        <div className={styles.toolbar}>
          <div><h3>外观皮肤</h3><span>全局令牌、组件属性、九宫格气泡和全站预览</span></div>
          <div className={styles.actions}>
            <button type="button" onClick={() => { setMode('publish'); setSelectedPublish([]); }}>发布管理</button>
            <button type="button" className={styles.primary} disabled={busy} onClick={() => void createSkin()}>新建皮肤</button>
          </div>
        </div>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        {notice ? <p className={styles.notice} role="status">{notice}</p> : null}
        <div className={styles.list}>
          {skins.map((skin) => (
            <article key={skin.id} className={styles.skinRow}>
              <div
                className={styles.previewThumb}
                style={skin.previewUrl ? { backgroundImage: `url("${skin.previewUrl}")` } : undefined}
              />
              <div className={styles.skinCopy}>
                <strong>{skin.name}{skin.isDefault ? ' · 默认' : ''}</strong>
                <span>{skin.listed ? '已上架' : '已下架'} · 草稿 v{String(skin.latestVersion)} · 发布 v{skin.publishedVersion ?? '-'}</span>
              </div>
              <div className={styles.rowActions}>
                <button type="button" disabled={busy} onClick={() => void openEditor(skin)}>编辑</button>
                {!skin.isDefault ? <button type="button" disabled={busy || skin.publishedVersion === null} onClick={() => void setDefaultSkin(skin.id).then(load)}>设默认</button> : null}
                {skin.publishedVersion === null ? <button type="button" className={styles.danger} disabled={busy} onClick={() => void deleteAdminSkin(skin.id).then(load)}>删除</button> : null}
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  }

  if (mode === 'publish') {
    return (
      <section className={styles.panel}>
        <div className={styles.toolbar}>
          <h3>发布管理</h3>
          <button type="button" onClick={() => setMode('list')}>返回列表</button>
        </div>
        <Field label="发布说明" wide>
          <input value={releaseNotes} maxLength={2000} onChange={(event) => setReleaseNotes(event.currentTarget.value)} />
        </Field>
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        {notice ? <p className={styles.notice} role="status">{notice}</p> : null}
        <div className={styles.publishActions}>
          <button type="button" className={styles.primary} disabled={busy || selectedPublish.length === 0} onClick={() => void publish('publish')}>上架所选</button>
          <button type="button" className={styles.danger} disabled={busy || selectedPublish.length === 0} onClick={() => void publish('unpublish')}>下架所选</button>
        </div>
        <div className={styles.list}>
          {skins.map((skin) => (
            <label key={skin.id} className={styles.publishRow}>
              <input
                type="checkbox"
                checked={selectedPublish.includes(skin.id)}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  setSelectedPublish((current) => checked ? [...current, skin.id] : current.filter((id) => id !== skin.id));
                }}
              />
              <span>
                <strong>{skin.name}{skin.isDefault ? ' · 默认' : ''}</strong>
                <small>{skin.listed ? '已上架' : '已下架'} · 最新 {statusLabel(skin.latestStatus)} v{String(skin.latestVersion)}</small>
              </span>
            </label>
          ))}
        </div>
      </section>
    );
  }

  if (!editorSkin || !manifest || !tokens) {
    return <p className={styles.error}>{error || '正在加载编辑器…'}</p>;
  }

  return (
    <section className={styles.editor}>
      <header className={styles.editorHeader}>
        <button type="button" onClick={exitEditor}>退出</button>
        <div>
          <strong>{editorSkin.name}</strong>
          <small>草稿 v{String(manifest.version.version)}{dirty ? ' · 未保存' : ''}</small>
        </div>
        <div className={styles.actions}>
          <button type="button" disabled={history.length === 0} onClick={undo}>撤销</button>
          <button type="button" className={styles.primary} disabled={busy || !dirty} onClick={() => void save()}>保存</button>
        </div>
      </header>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      {notice ? <p className={styles.notice} role="status">{notice}</p> : null}
      <div className={styles.workspace}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHead}>
            <strong>页面与状态</strong>
            <button type="button" data-active={selectMode ? 'true' : 'false'} onClick={() => setSelectMode((value) => !value)}>
              {selectMode ? '点击画布选择' : '选择页面组件'}
            </button>
          </div>
          <nav className={styles.pageList} aria-label="预览页面">
            {PREVIEW_PAGES.map((entry) => (
              <button
                key={entry.id}
                type="button"
                data-active={entry.id === previewPage ? 'true' : 'false'}
                onClick={() => { setPreviewPage(entry.id); setPreviewStateId('base'); }}
              >
                <AppIcon name={entry.icon} size={15} />{entry.label}
              </button>
            ))}
          </nav>
          <label className={styles.stateSelect}>预览状态
            <select value={previewState.id} onChange={(event) => setPreviewStateId(event.currentTarget.value)}>
              {previewStates.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <input
            type="search"
            aria-label="搜索页面组件"
            placeholder="搜索页面、组件名称或 id"
            value={componentQuery}
            onChange={(event) => setComponentQuery(event.currentTarget.value)}
          />
          <div className={styles.componentList}>
            {componentOptions.map((entry) => (
              <button
                key={entry.id}
                type="button"
                data-active={entry.id === selectedId ? 'true' : 'false'}
                onClick={() => {
                  setSelectedId(entry.id);
                  setSelectedLabel(entry.label);
                  setInspectorTab('component');
                }}
              >
                <span>{entry.label}</span><small>{entry.page} · {entry.id}</small>
              </button>
            ))}
          </div>
        </aside>
        <div className={styles.previewPane}>
          <PreviewSurface
            tokens={tokens}
            page={previewPage}
            state={previewState}
            selectMode={selectMode}
            selectedId={selectedId}
            onSelect={(id) => {
              const found = discovered.find((item) => item.id === id);
              setSelectedId(id);
              setSelectedLabel(found?.label ?? id);
              setInspectorTab('component');
            }}
            rootRef={previewRef}
            {...previewAssets}
          />
        </div>
        <aside className={styles.inspectorPane}>
          <div className={styles.inspectorTabs} role="tablist" aria-label="皮肤编辑分类">
            <button type="button" role="tab" aria-selected={inspectorTab === 'global'} onClick={() => setInspectorTab('global')}>全局</button>
            <button type="button" role="tab" aria-selected={inspectorTab === 'component'} onClick={() => setInspectorTab('component')}>组件</button>
            <button type="button" role="tab" aria-selected={inspectorTab === 'assets'} onClick={() => setInspectorTab('assets')}>素材</button>
          </div>
          {inspectorTab === 'global' ? (
            <TokenInspector
              tokens={tokens}
              onChange={commit}
              onUploadBubble={(role, file) => void upload('bubble', role, file)}
            />
          ) : inspectorTab === 'component' ? (
            selectedId ? (
              <ComponentInspector
                skinId={selectedId}
                label={selectedLabel || selectedId}
                tokens={tokens}
                onChange={commit}
                onReset={() => {
                  const next = cloneTokens(tokens);
                  delete next.componentOverrides[selectedId];
                  commit(next);
                }}
              />
            ) : <p className={styles.empty}>从左侧组件树或画布中选择一个组件。</p>
          ) : (
            <div className={styles.materialPanel}>
              <h4>皮肤素材</h4>
              <Field label="背景图">
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => void upload('wallpaper', 'main', event.currentTarget.files?.[0])} />
              </Field>
              <Field label="预览图">
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => void upload('preview', 'main', event.currentTarget.files?.[0])} />
              </Field>
              <Field label="正文字体">
                <input type="file" accept=".woff2,font/woff2" onChange={(event) => void upload('font', 'body', event.currentTarget.files?.[0])} />
              </Field>
              <Field label="标题字体">
                <input type="file" accept=".woff2,font/woff2" onChange={(event) => void upload('font', 'heading', event.currentTarget.files?.[0])} />
              </Field>
              <Field label="图标槽位">
                <input value={iconSlot} onChange={(event) => setIconSlot(event.currentTarget.value)} />
              </Field>
              <Field label="图标 SVG">
                <input type="file" accept=".svg,image/svg+xml" onChange={(event) => {
                  const slot = iconSlot.trim();
                  if (!slot) {
                    setError('请先填写图标槽位');
                    return;
                  }
                  void upload('icon', slot, event.currentTarget.files?.[0]);
                }} />
              </Field>
              <div className={styles.assetList}>
                {manifest.assets.map((asset) => (
                  <span key={asset.id}>
                    {asset.kind}:{asset.slot}
                    <button type="button" onClick={() => void deleteAdminAsset(editorSkin.id, manifest.version.version, asset.kind, asset.slot).then(async () => {
                      setManifest(await getAdminVersion(editorSkin.id, manifest.version.version));
                      await load();
                    })}>移除</button>
                  </span>
                ))}
              </div>
              <Field label="发布说明" wide>
                <input value={releaseNotes} onChange={(event) => setReleaseNotes(event.currentTarget.value)} />
              </Field>
              <button type="button" className={styles.resetAll} onClick={() => {
                const next = cloneTokens(tokens);
                next.componentOverrides = {};
                commit(next);
              }}>恢复整套组件默认</button>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
